import { useEffect, useRef, useMemo } from 'react';
import { useDebounce } from '../hooks/useDebounce.js';
import { renderCanvas } from '../utils/canvasRenderer.js';

const CANVAS_SIZE = 1080;
const PREVIEW_SCALE = 0.5;
const DEBOUNCE_DELAY = 400;

export default function CanvasRenderer({ storyData }) {
  const canvasRef = useRef(null);

  const textData = useMemo(
    () => ({
      headline: storyData.headline,
      subtitle: storyData.subtitle || '',
      category: storyData.category,
      authorName: storyData.authorName,
      authorRole: storyData.authorRole,
    }),
    [storyData.headline, storyData.subtitle, storyData.category, storyData.authorName, storyData.authorRole]
  );

  const debouncedTextData = useDebounce(textData, DEBOUNCE_DELAY);

  const renderData = useMemo(
    () => ({
      ...debouncedTextData,
      backgroundImage: storyData.backgroundImage,
      titlePosition: storyData.titlePosition,
    }),
    [debouncedTextData, storyData.backgroundImage, storyData.titlePosition]
  );

  useEffect(() => {
    if (!renderData.backgroundImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    canvas.style.width = `${CANVAS_SIZE * PREVIEW_SCALE}px`;
    canvas.style.height = `${CANVAS_SIZE * PREVIEW_SCALE}px`;

    let cancelled = false;

    const draw = async () => {
      try {
        await renderCanvas(ctx, renderData, CANVAS_SIZE);
      } catch (err) {
        if (!cancelled) {
          console.error('Canvas preview render error:', err);
        }
      }
    };

    draw();

    return () => {
      cancelled = true;
    };
  }, [renderData]);

  return (
    <div className="canvas-container">
      <canvas ref={canvasRef} />
    </div>
  );
}
